// ===========================================
// Roadmap Card - Compact Draggable Feature Request Card
// ===========================================
// Displays a single feature request with priority dropdown
// Designed for compact display in kanban columns

import React, { useState, useEffect, useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useProjects } from '../context/ProjectContext';
import { 
  GripVertical, 
  Calendar,
  ChevronDown,
  Flag
} from 'lucide-react';

// ===========================================
// Priority Configuration
// ===========================================
// Color-coded priority levels for visual distinction
const PRIORITY_CONFIG = {
  low: { label: 'Low', color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.15)' },
  medium: { label: 'Med', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)' },
  high: { label: 'High', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)' }
};

const RoadmapCard = ({ item, projectId, isDragging, onEdit }) => {
  const { updateMilestone } = useProjects();
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const dropdownRef = useRef(null);
  
  // ===========================================
  // Setup sortable drag functionality
  // ===========================================
  // Get item ID (handle both _id and id for MongoDB compatibility)
  const itemId = item._id || item.id;
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging
  } = useSortable({ 
    id: itemId,
    data: { item }
  });

  // Apply transform styles for drag animation
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortableDragging ? 0.5 : 1
  };

  // Get current priority config (fallback to medium if undefined)
  const currentPriority = PRIORITY_CONFIG[item.priority] || PRIORITY_CONFIG.medium;

  // ===========================================
  // Close dropdown when clicking outside
  // ===========================================
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowPriorityDropdown(false);
      }
    };

    if (showPriorityDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPriorityDropdown]);

  // ===========================================
  // Handle priority change
  // ===========================================
  const handlePriorityChange = async (newPriority) => {
    setShowPriorityDropdown(false);
    if (newPriority !== item.priority) {
      // Use itemId variable for API call (handles both _id and id)
      await updateMilestone(projectId, itemId, { priority: newPriority });
    }
  };

  // ===========================================
  // Format target date for display
  // ===========================================
  const formatDate = (date) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // ===========================================
  // Handle card click to edit (exclude drag handle and priority)
  // ===========================================
  const handleCardClick = (e) => {
    // Don't trigger edit if clicking the drag handle or priority dropdown
    if (e.target.closest('.roadmap-card__drag-handle') ||
        e.target.closest('.roadmap-card__priority-wrapper')) {
      return;
    }
    // Trigger edit callback with this item's data
    if (onEdit) {
      onEdit(item);
    }
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`roadmap-card ${isSortableDragging ? 'roadmap-card--dragging' : ''}`}
      onClick={handleCardClick}
    >
      {/* Drag Handle - grab here to drag the card */}
      <div 
        className="roadmap-card__drag-handle"
        {...attributes}
        {...listeners}
      >
        <GripVertical size={12} />
      </div>

      {/* Card Content */}
      <div className="roadmap-card__content">
        {/* Title */}
        <div className="roadmap-card__header">
          <h4 className="roadmap-card__title">{item.milestone}</h4>
        </div>

        {/* Description (truncated to 2 lines) */}
        {item.description && (
          <p className="roadmap-card__description">
            {item.description.length > 80 
              ? `${item.description.substring(0, 80)}...` 
              : item.description}
          </p>
        )}

        {/* Footer: Priority Dropdown & Target Date */}
        <div className="roadmap-card__footer">
          {/* Priority Dropdown */}
          <div className="roadmap-card__priority-wrapper" ref={dropdownRef}>
            <button
              className="roadmap-card__priority"
              style={{ 
                background: currentPriority.bg,
                borderColor: currentPriority.color 
              }}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                setShowPriorityDropdown(!showPriorityDropdown);
              }}
            >
              <Flag size={10} style={{ color: currentPriority.color }} />
              <span style={{ color: currentPriority.color }}>
                {currentPriority.label}
              </span>
              <ChevronDown size={10} style={{ color: currentPriority.color }} />
            </button>

            {/* Priority Dropdown Menu */}
            {showPriorityDropdown && (
              <div className="roadmap-card__priority-dropdown">
                {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                  <button
                    key={key}
                    className={`roadmap-card__priority-option ${item.priority === key ? 'active' : ''}`}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePriorityChange(key);
                    }}
                  >
                    <Flag size={10} style={{ color: config.color }} />
                    <span>{config.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Target Date Badge */}
          {item.targetDate && (
            <span className="roadmap-card__date">
              <Calendar size={10} />
              {formatDate(item.targetDate)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoadmapCard;

